import { apiClient, newIdempotencyKey, type ApiResult } from './client'

/**
 * `GET/POST/PATCH/DELETE /api/v1/saved-views` (docs/api-spec.md §25, FR-M23.2, ADR-0058/ADR-0059)
 * — the server-persisted saved-view store behind `lib/savedViews.ts`. Personal (`user`) scope is
 * unrestricted; `scope="tenant"` (IB-27 S4a) is capability-gated server-side (`saved_view:A`,
 * denied → `403`) — this client never pre-checks that, it just relays the request/response.
 * `scope="team"` remains rejected `422` (RAID R-35, deferred to S4b), never a silent downgrade.
 * This client is a thin HTTP wrapper only.
 */

export type SavedViewScope = 'user' | 'team' | 'tenant'

export interface SavedViewResponse {
  id: string
  listId: string
  name: string
  scope: SavedViewScope
  isDefault: boolean
  state: unknown
  createdAt: string
  updatedAt: string
  version: number
}

export interface CreateSavedViewRequestBody {
  listId: string
  name: string
  state: unknown
  scope?: SavedViewScope
  isDefault?: boolean
}

export interface PatchSavedViewRequestBody {
  name?: string
  state?: unknown
  isDefault?: boolean
}

/** `listId`-scoped — returns the caller's own personal (`user`) rows UNION the tenant's shared
 * `tenant`-default row, if any (IB-27 S4a widened read, no capability needed to see it). Flat/
 * unpaginated (small N per user+list). */
export async function listSavedViews(listId: string, signal?: AbortSignal): Promise<SavedViewResponse[]> {
  const { data } = await apiClient.get<SavedViewResponse[]>(
    `/saved-views?listId=${encodeURIComponent(listId)}`,
    { signal },
  )
  return data
}

export async function createSavedView(body: CreateSavedViewRequestBody): Promise<ApiResult<SavedViewResponse>> {
  return apiClient.post<SavedViewResponse>('/saved-views', body, { idempotencyKey: newIdempotencyKey() })
}

export async function patchSavedView(
  id: string,
  body: PatchSavedViewRequestBody,
  ifMatchVersion: number,
): Promise<ApiResult<SavedViewResponse>> {
  return apiClient.patch<SavedViewResponse>(`/saved-views/${id}`, body, { ifMatchVersion })
}

export async function deleteSavedView(id: string, ifMatchVersion: number): Promise<void> {
  await apiClient.del(`/saved-views/${id}`, { ifMatchVersion })
}
